import React, { useState, useMemo, useEffect } from 'react';
import { Search, Users, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { getCouponService } from '../../../domains/coupon';
import useAuthStore from '../../../core/stores/authStore';
import { useTargetGroups, useSaveTargetGroup, useDeleteTargetGroup } from '../../hooks/coupon/useCouponTargeting';
import {
  toApiGender, genderLabel, agesToApi, agesToLabels, buildEligibleParams,
} from '../../hooks/coupon/couponTargeting';
import styles from './TargetGroupManagement.module.css';

const ITEMS_PER_PAGE = 10;

const AGE_MAP = {
  '20대': [20], '30대': [30], '40대': [40], '50대': [50],
  '60대': [60], '70대': [70], '80대+': [80, 90],
};

const TargetGroupManagement = () => {
  const pharmacy = useAuthStore(state => state.pharmacy);
  const { data: groups = [] } = useTargetGroups();
  const saveGroup = useSaveTargetGroup();
  const deleteGroup = useDeleteTargetGroup();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // 타겟 대상자 수 (eligible-count API)
  const [eligibleCount, setEligibleCount] = useState(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  // 새 그룹 폼
  const [newName, setNewName] = useState('');
  const [newGender, setNewGender] = useState('전체');
  const [newAgeRange, setNewAgeRange] = useState(['전체']);
  const [newDescription, setNewDescription] = useState('');

  const formTarget = {
    target_gender: toApiGender(newGender),
    target_ages: agesToApi(newAgeRange, AGE_MAP),
    target_conditions: [],
    last_visit_days_gte: null,
  };
  const eligibleParamsKey = JSON.stringify(buildEligibleParams(formTarget));

  // 모달 열려있을 때 + 필터 변경 시 디바운스로 eligible-count 호출
  useEffect(() => {
    if (!showModal) return undefined;
    const pharmacyId = pharmacy?.business_registration_number;
    if (!pharmacyId) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIsLoadingCount(true);
        const result = await getCouponService().getEligibleCount(pharmacyId, JSON.parse(eligibleParamsKey));
        if (!cancelled) setEligibleCount(result?.result_code === '200' ? result.data?.count ?? null : null);
      } catch {
        if (!cancelled) setEligibleCount(null);
      } finally {
        if (!cancelled) setIsLoadingCount(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showModal, eligibleParamsKey, pharmacy]);

  // 검색 필터
  const filteredGroups = useMemo(() => {
    let filtered = groups;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        g =>
          g.name.toLowerCase().includes(q) ||
          g.detail?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [groups, searchQuery]);

  // 페이지네이션
  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / ITEMS_PER_PAGE));
  const paginatedGroups = filteredGroups.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // 검색 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleAgeToggle = (age) => {
    if (age === '전체') {
      setNewAgeRange(['전체']);
      return;
    }
    setNewAgeRange(prev => {
      const withoutAll = prev.filter(a => a !== '전체');
      if (withoutAll.includes(age)) {
        const next = withoutAll.filter(a => a !== age);
        return next.length === 0 ? ['전체'] : next;
      }
      return [...withoutAll, age];
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setNewName('');
    setNewGender('전체');
    setNewAgeRange(['전체']);
    setNewDescription('');
  };

  const handleCreate = async () => {
    if (!newName.trim() || saveGroup.isPending) return;
    try {
      await saveGroup.mutateAsync({
        name: newName.trim(),
        detail: newDescription,
        ...formTarget,
      });
      closeModal();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = (groupId) => {
    deleteGroup.mutate(groupId, { onError: (error) => alert(error.message) });
  };

  const handleSearch = () => {
    // 검색 버튼 클릭 시 동작 (이미 searchQuery 기반 필터링됨)
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className={styles.pageContainer}>
        {/* Main Card */}
        <div className={styles.historySection}>
          {/* Header */}
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIconBadge}>
                <Users size={20} color="#3C4651" />
              </div>
              <div className={styles.headerTitleWrap}>
                <span className={styles.sectionTitle}>타겟 그룹</span>
                <span className={styles.groupCount}>저장된 그룹 {groups.length}개</span>
              </div>
            </div>
            <button className={styles.createButton} onClick={() => setShowModal(true)}>
              + 새로운 그룹 생성
            </button>
          </div>

          {/* Search Bar */}
          <div className={styles.searchRow}>
            <div className={styles.searchInputWrap}>
              <div className={styles.searchIconInner}>
                <Search size={20} />
              </div>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="설명으로 검색"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <button className={styles.searchButton} onClick={handleSearch}>
              검색
            </button>
          </div>

          {/* Table */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.colGroupName}>그룹명</th>
                  <th className={styles.colGender}>성별</th>
                  <th className={styles.colAge}>연령대</th>
                  <th className={styles.colUserCount}>대상인원</th>
                  <th className={styles.colCreatedAt}>생성일</th>
                  <th className={styles.colManage}>관리</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.length > 0 ? (
                  paginatedGroups.map(group => (
                    <tr key={group.id} className={styles.tableRow}>
                      <td className={styles.groupName}>{group.name}</td>
                      <td className={styles.cellCenter}>
                        {genderLabel(group.target_gender)}
                      </td>
                      <td className={styles.cellCenter}>{agesToLabels(group.target_ages, AGE_MAP).join(', ') || '전체'}</td>
                      <td className={styles.cellCenter}>{group.count != null ? `${group.count}명` : '-'}</td>
                      <td className={styles.cellCenter}>{group.created_at ? group.created_at.slice(0, 10) : '-'}</td>
                      <td>
                        <div className={styles.manageCell}>
                          <button className={styles.editButton}>
                            수정
                          </button>
                          <button
                            className={styles.deleteButton}
                            disabled={deleteGroup.isPending}
                            onClick={() => handleDelete(group.id)}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      <FileText className={styles.emptyIcon} />
                      <div className={styles.emptyText}>저장된 타겟 그룹이 없습니다</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredGroups.length > 0 && (
            <div className={styles.paginationBar}>
              <div className={styles.paginationInfo}>
                검색결과 {filteredGroups.length}개
              </div>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.paginationText}>
                  {currentPage}/{totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 생성 모달 */}
        {showModal && (
          <div className={styles.modalOverlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              {/* Modal Header */}
              <div className={styles.modalHeader}>
                <div className={styles.modalIconBadge}>
                  <Users size={20} color="#3C4651" />
                </div>
                <h2 className={styles.modalTitle}>새로운 타겟 그룹 생성</h2>
              </div>

              {/* 그룹명 */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>그룹명</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="예) 30대 여성 고객"
                />
              </div>

              {/* 성별 */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>성별</label>
                <div className={styles.chipGroup}>
                  {['전체', 'M', 'F'].map(g => (
                    <button
                      key={g}
                      className={`${styles.chip} ${newGender === g ? styles.chipActive : ''}`}
                      onClick={() => setNewGender(g)}
                    >
                      {g === 'M' ? '남성' : g === 'F' ? '여성' : '전체'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 연령대 - 복수 선택 */}
              <div className={styles.formGroup}>
                <div className={styles.formLabelRow}>
                  <label className={styles.formLabel}>연령대</label>
                  <span className={styles.formLabelSub}>복수선택 가능</span>
                </div>
                <div className={styles.chipGroup}>
                  {['전체', '20대', '30대', '40대', '50대', '60대', '70대', '80대+'].map(a => (
                    <button
                      key={a}
                      className={`${styles.chip} ${newAgeRange.includes(a) ? styles.chipActive : ''}`}
                      onClick={() => handleAgeToggle(a)}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              {/* 설명 */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>설명</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={newDescription}
                  onChange={e => setNewDescription(e.target.value)}
                  placeholder="타겟 그룹에 대한 설명을 입력하세요"
                />
              </div>

              {/* 대상자 요약 */}
              <div className={styles.preview}>
                {isLoadingCount ? (
                  <span className={styles.previewLabel}>대상자 수 조회중...</span>
                ) : (
                  <>
                    <span className={styles.previewLabel}>해당 조건에 맞는 대상자</span>
                    <span className={styles.previewCount}>{eligibleCount != null ? `약 ${eligibleCount}명` : '-'}</span>
                  </>
                )}
              </div>

              {/* 하단 버튼 */}
              <div className={styles.modalActions}>
                <button className={styles.cancelButton} onClick={closeModal}>
                  취소
                </button>
                <button
                  className={styles.submitButton}
                  onClick={handleCreate}
                  disabled={!newName.trim()}
                >
                  생성
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default TargetGroupManagement;
